'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI, eventsAPI, expensesAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import '../styles/Dashboard.css';

function Dashboard() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const { error: showError } = useToast();

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, eventsRes, expensesRes] = await Promise.all([
        reportsAPI.getMonthlySummary(month),
        eventsAPI.getAll(),
        expensesAPI.getAll()
      ]);
      setSummary(summaryRes.data.report);
      setEvents(eventsRes.data.events);
      setExpenses(expensesRes.data.expenses);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch dashboard data';
      showError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [month, showError]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Prepare chart data
  const expenseBreakdownData = () => {
    const categoryTotals = {};
    const monthStart = new Date(month + '-01');
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    
    expenses
      .filter(exp => {
        const expDate = new Date(exp.date);
        return expDate >= monthStart && expDate <= monthEnd;
      })
      .forEach(exp => {
        categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + exp.amount;
      });

    return Object.entries(categoryTotals).map(([name, value]) => ({ name, value }));
  };

  const incomeExpenseData = () => {
    return [
      {
        name: 'Income',
        amount: summary?.totalIncome || 0
      },
      {
        name: 'Expenses',
        amount: summary?.totalExpenses || 0
      }
    ];
  };

  // Get upcoming events
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.eventDate);
    return eventDate >= new Date() && event.status === 'upcoming';
  }).slice(0, 5);

  // Get pending payments (balance > 0)
  const pendingPayments = events.filter(event => event.balanceAmount > 0).slice(0, 5);

  // Quick stats
  const quickStats = {
    upcomingEvents: upcomingEvents.length,
    completedEvents: events.filter(e => e.status === 'completed').length,
    pendingPayments: pendingPayments.reduce((sum, e) => sum + e.balanceAmount, 0),
    totalEvents: events.length
  };

  const COLORS = ['#3498db', '#e74c3c', '#f39c12', '#27ae60', '#9b59b6', '#1abc9c'];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Monthly Overview</h1>
          <p className="dashboard-subtitle">
            See how your catering business is performing this month at a glance.
          </p>
        </div>
        <div className="month-selector">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="quick-stat-card">
          <div className="stat-icon upcoming">📅</div>
          <div className="stat-info">
            <h4>Upcoming Events</h4>
            <p>{quickStats.upcomingEvents}</p>
          </div>
        </div>
        <div className="quick-stat-card">
          <div className="stat-icon completed">✓</div>
          <div className="stat-info">
            <h4>Completed</h4>
            <p>{quickStats.completedEvents}</p>
          </div>
        </div>
        <div className="quick-stat-card">
          <div className="stat-icon pending">₹</div>
          <div className="stat-info">
            <h4>Pending Payments</h4>
            <p>₹{quickStats.pendingPayments.toFixed(2)}</p>
          </div>
        </div>
        <div className="quick-stat-card">
          <div className="stat-icon total">📊</div>
          <div className="stat-info">
            <h4>Total Events</h4>
            <p>{quickStats.totalEvents}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="summary-cards">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : summary ? (
        <>
          <div className="summary-cards">
            <div className="card income-card">
              <h3>Total Income</h3>
              <p className="amount">₹{summary.totalIncome?.toFixed(2) || '0.00'}</p>
              <small>{summary.numberOfEvents || 0} events</small>
            </div>

            <div className="card expense-card">
              <h3>Event Expenses</h3>
              <p className="amount">₹{summary.totalEventExpenses?.toFixed(2) || '0.00'}</p>
            </div>

            <div className="card salary-card">
              <h3>Salary Paid</h3>
              <p className="amount">₹{summary.totalSalaryPaid?.toFixed(2) || '0.00'}</p>
            </div>

            <div className="card total-expense-card">
              <h3>Total Expenses</h3>
              <p className="amount">₹{summary.totalExpenses?.toFixed(2) || '0.00'}</p>
            </div>

            <div className={`card profit-card ${summary.netProfitOrLoss >= 0 ? 'positive' : 'negative'}`}>
              <h3>Net Profit/Loss</h3>
              <p className="amount">₹{summary.netProfitOrLoss?.toFixed(2) || '0.00'}</p>
              <small>{summary.netProfitOrLoss >= 0 ? 'Profit' : 'Loss'}</small>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            <div className="chart-card">
              <h3>Income vs Expenses</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={incomeExpenseData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="amount" fill="#3498db" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Expense Breakdown by Category</h3>
              {expenseBreakdownData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={expenseBreakdownData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {expenseBreakdownData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-chart-data">No expense data for this month</div>
              )}
            </div>
          </div>

          {/* Recent Events & Pending Payments */}
          <div className="dashboard-lists">
            <div className="list-card">
              <div className="list-header">
                <h3>Upcoming Events</h3>
                <Link to="/events" className="view-all">View All →</Link>
              </div>
              {upcomingEvents.length > 0 ? (
                <ul className="event-list">
                  {upcomingEvents.map(event => (
                    <li key={event._id}>
                      <div className="event-item">
                        <div>
                          <strong>{event.eventName}</strong>
                          <span className="client">{event.clientName}</span>
                        </div>
                        <div className="event-meta">
                          <span>{new Date(event.eventDate).toLocaleDateString()}</span>
                          <span className="amount">₹{event.totalBill.toFixed(2)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">No upcoming events</div>
              )}
            </div>

            <div className="list-card">
              <div className="list-header">
                <h3>Pending Payments</h3>
                <Link to="/events" className="view-all">View All →</Link>
              </div>
              {pendingPayments.length > 0 ? (
                <ul className="event-list">
                  {pendingPayments.map(event => (
                    <li key={event._id}>
                      <div className="event-item">
                        <div>
                          <strong>{event.eventName}</strong>
                          <span className="client">{event.clientName}</span>
                        </div>
                        <div className="event-meta">
                          <span>Balance: ₹{event.balanceAmount.toFixed(2)}</span>
                          <span className="status upcoming-badge">Pending</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">No pending payments</div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default Dashboard;
